import React, { useState } from 'react';
import Modal from './Modal';
import { uploadHomeAdImage } from '../api/skipzyAdmin';

/**
 * Create/edit form for one Home tab campaign card.
 *
 * Everything a card and its offer page show lives on the ad row, so this form
 * is the whole surface. The notes beside each field say where it lands in the
 * app — an ad is written blind otherwise, and several of these fields are
 * invisible until a parent has already tapped the card.
 *
 * Images upload the moment a file is picked and only the returned CloudFront
 * URL is held in state, so Save is a plain JSON write and a half-finished
 * upload never leaves a partial record.
 */

// The size vocabulary the whole app speaks — mobile's CHILD_SIZES, and the same
// list the Skipzy backend validates against. A band outside it is refused there.
const CHILD_SIZES = [
  '0-1M', '1-3M', '3-6M', '6-12M', '12-18M', '18-24M',
  '2-3Y', '3-4Y', '4-5Y', '5-6Y', '6-7Y', '7-8Y',
];
const SIZE_PRESETS = {
  All: CHILD_SIZES,
  'Walking up': CHILD_SIZES.slice(4),
  Infant: CHILD_SIZES.slice(0, 3),
};

const EMPTY = {
  slug: '',
  name: '',
  header_text: '',
  description: '',
  hero_image: '',
  banner_images: [],
  width: 'full',
  attributes: [],
  questions: [],
  genders: [],
  sizes: [],
};

const linesToList = text => text.split('\n').map(s => s.trim()).filter(Boolean);
const listToLines = list => (list || []).join('\n');

const INPUT = 'w-full border border-gray-200 rounded px-3 py-2 text-sm';
const LABEL = 'block text-xs font-semibold text-gray-600 mb-1';
const HINT = 'text-xs text-gray-400 mt-1';

function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className={LABEL}>{label}</label>
      {children}
      {hint && <p className={HINT}>{hint}</p>}
    </div>
  );
}

/** One uploaded creative: thumbnail plus whatever controls the caller needs. */
function Thumb({ url, children }) {
  return (
    <div className="relative w-20 h-24 rounded overflow-hidden border border-gray-200 bg-gray-50 shrink-0">
      <img src={url} alt="" className="w-full h-full object-cover" />
      {children}
    </div>
  );
}

export default function HomeAdEditor({ ad, onClose, onSave }) {
  const editing = Boolean(ad);
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(ad || {}),
    description: ad?.description || '',
    hero_image: ad?.hero_image || '',
  }));
  const [uploading, setUploading] = useState(null); // 'hero' | 'banner' | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  async function upload(file, target) {
    if (!file) return;
    setUploading(target);
    setError(null);
    try {
      const url = await uploadHomeAdImage(file);
      if (target === 'hero') set('hero_image', url);
      else set('banner_images', [...form.banner_images, url]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(null);
    }
  }

  function toggleFrom(key, value) {
    const current = form[key] || [];
    set(key, current.includes(value) ? current.filter(v => v !== value) : [...current, value]);
  }

  function moveBanner(index, delta) {
    const next = [...form.banner_images];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set('banner_images', next);
  }

  function patchQuestion(index, patch) {
    set('questions', form.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  }

  /**
   * The rules worth catching before the round trip. The backend enforces all of
   * these too — this is so the message names the field rather than arriving as
   * a 422 body.
   *
   * Genders and sizes are the two that matter most: the app only demands a fit
   * from the parent when the ad actually offers one, so an ad saved without
   * them books cleanly and reaches a stylist with nothing to pack against.
   */
  function validate() {
    if (!editing && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) {
      return 'Slug must be lowercase words joined by hyphens, e.g. love-for-desi.';
    }
    if (!form.name.trim()) return 'Campaign name is required.';
    if (!form.header_text.trim()) return 'Headline is required.';
    if (!form.hero_image) return 'A hero image is required — it is the card itself.';
    if (!form.genders.length) return 'Pick at least one gender.';
    if (!form.sizes.length) return 'Pick at least one size.';
    const prompts = form.questions.map(q => q.prompt.trim());
    if (prompts.some(p => !p)) return 'Every question needs a prompt.';
    if (new Set(prompts).size !== prompts.length) {
      return 'Two questions cannot share the same prompt.';
    }
    if (form.questions.some(q => !(q.options || []).length)) {
      return 'Every question needs at least one option.';
    }
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);

    const payload = {
      name: form.name.trim(),
      header_text: form.header_text.trim(),
      description: form.description.trim() || null,
      hero_image: form.hero_image,
      banner_images: form.banner_images,
      width: form.width,
      attributes: form.attributes,
      questions: form.questions.map(q => ({
        prompt: q.prompt.trim(),
        max_select: Number(q.max_select) || 1,
        options: q.options,
      })),
      genders: form.genders,
      sizes: form.sizes,
    };
    // Slug is this ad's identity — in the URL, in analytics, and on every claim
    // already frozen against it — so it is set once and never patched.
    if (!editing) payload.slug = form.slug.trim();

    setSaving(true);
    setError(null);
    try {
      await onSave(payload, editing ? form.slug : null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not save the ad');
      setSaving(false);
    }
  }

  const busy = saving || Boolean(uploading);

  return (
    <Modal title={editing ? `Edit ${form.slug}` : 'New home ad'} onClose={onClose} wide>
      <form onSubmit={submit}>
        {error && (
          <div className="mb-4 bg-red-50 border border-red-100 text-zoddle-pink text-sm px-4 py-3 rounded">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        <Field
          label="Slug *"
          hint={
            editing
              ? 'Fixed — this is the ad’s identity in analytics and on existing claims.'
              : 'Lowercase, hyphenated. Never shown to a parent.'
          }
        >
          <input
            className={`${INPUT} ${editing ? 'bg-gray-50 text-gray-500' : ''}`}
            value={form.slug}
            disabled={editing}
            placeholder="love-for-desi"
            onChange={e => set('slug', e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Campaign name *" hint="The card’s eyebrow, and the occasion on the booking.">
            <input
              className={INPUT}
              value={form.name}
              placeholder="Festive / Ethnic"
              onChange={e => set('name', e.target.value)}
            />
          </Field>
          <Field label="Headline *" hint="The big line on the card and on the offer page.">
            <input
              className={INPUT}
              value={form.header_text}
              placeholder="Love for Desi"
              onChange={e => set('header_text', e.target.value)}
            />
          </Field>
        </div>
        {form.name.trim() && form.name.trim() === form.header_text.trim() && (
          <p className="-mt-2 mb-4 text-xs text-gray-400">
            Name and headline match, so the card shows the headline only — that’s intended for
            most ads.
          </p>
        )}

        <Field label="Description" hint="One line on the offer page. Never appears on the card.">
          <input
            className={INPUT}
            value={form.description}
            placeholder="Casual ethnic pieces"
            onChange={e => set('description', e.target.value)}
          />
        </Field>

        <Field label="Width *" hint="Full owns a whole page; halves pair two-per-page.">
          <div className="flex gap-4 text-sm text-gray-700">
            {['full', 'half'].map(w => (
              <label key={w} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={form.width === w}
                  onChange={() => set('width', w)}
                />
                {w === 'full' ? 'Full page' : 'Half page'}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Hero image *" hint="This is the card — full-bleed, and the whole tap target.">
          <div className="flex items-start gap-3">
            {form.hero_image && <Thumb url={form.hero_image} />}
            <div className="flex-1">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={busy}
                className="text-sm"
                onChange={e => upload(e.target.files?.[0], 'hero')}
              />
              {uploading === 'hero' && <p className={HINT}>Uploading…</p>}
              {form.hero_image && (
                <p className="text-xs text-gray-400 mt-1 break-all">{form.hero_image}</p>
              )}
            </div>
          </div>
        </Field>

        <Field
          label="Gallery"
          hint="Swiped through on the offer page. Leave empty and it repeats the hero."
        >
          <div className="flex items-start gap-2 flex-wrap">
            {form.banner_images.map((url, i) => (
              <div key={url} className="text-center">
                <Thumb url={url}>
                  <button
                    type="button"
                    onClick={() =>
                      set('banner_images', form.banner_images.filter(u => u !== url))
                    }
                    className="absolute top-0 right-0 bg-black bg-opacity-50 text-white w-5 h-5 leading-none"
                  >
                    ×
                  </button>
                </Thumb>
                <div className="flex justify-center gap-1 mt-1 text-gray-400">
                  <button type="button" onClick={() => moveBanner(i, -1)}>←</button>
                  <button type="button" onClick={() => moveBanner(i, 1)}>→</button>
                </div>
              </div>
            ))}
            <label className="w-20 h-24 border border-dashed border-gray-300 rounded flex items-center justify-center text-2xl text-gray-300 cursor-pointer shrink-0">
              +
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={busy}
                className="hidden"
                onChange={e => {
                  upload(e.target.files?.[0], 'banner');
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          {uploading === 'banner' && <p className={HINT}>Uploading…</p>}
        </Field>

        <Field
          label="Promises"
          hint="One per line. Printed under the headline, and passed to the stylists on the booking."
        >
          <textarea
            className={INPUT}
            rows={3}
            value={listToLines(form.attributes)}
            placeholder={'Kurtas and kurta sets\nSimple and elegant'}
            onChange={e => set('attributes', linesToList(e.target.value))}
          />
        </Field>

        <Field label="Genders *" hint="The offer page shows only these — a boys’ ad never offers a frock.">
          <div className="flex gap-4 text-sm text-gray-700">
            {['boy', 'girl'].map(g => (
              <label key={g} className="flex items-center gap-2 cursor-pointer capitalize">
                <input
                  type="checkbox"
                  checked={form.genders.includes(g)}
                  onChange={() => toggleFrom('genders', g)}
                />
                {g}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Sizes *" hint="Without these no size is collected at all, and the stylist packs blind.">
          <div className="flex gap-2 mb-2">
            {Object.entries(SIZE_PRESETS).map(([label, sizes]) => (
              <button
                key={label}
                type="button"
                onClick={() => set('sizes', sizes)}
                className="text-xs font-semibold text-gray-500 hover:text-zoddle-green"
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => set('sizes', [])}
              className="text-xs font-semibold text-gray-400 hover:text-zoddle-pink"
            >
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CHILD_SIZES.map(size => {
              const on = form.sizes.includes(size);
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => toggleFrom('sizes', size)}
                  className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                    on
                      ? 'bg-zoddle-green text-white border-transparent'
                      : 'bg-white text-gray-500 border-gray-200'
                  }`}
                >
                  {size}
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label="Questions"
          hint="Asked as option pills before booking. The answers reach the stylists."
        >
          {form.questions.map((q, i) => (
            <div key={i} className="border border-gray-200 rounded p-3 mb-2">
              <div className="flex gap-2 mb-2">
                <input
                  className={INPUT}
                  value={q.prompt}
                  placeholder="Colour Choices"
                  onChange={e => patchQuestion(i, { prompt: e.target.value })}
                />
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="w-24 border border-gray-200 rounded px-3 py-2 text-sm"
                  value={q.max_select}
                  title="How many options a parent may pick"
                  onChange={e => patchQuestion(i, { max_select: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => set('questions', form.questions.filter((_, j) => j !== i))}
                  className="text-gray-400 hover:text-zoddle-pink px-2"
                >
                  ×
                </button>
              </div>
              <textarea
                className={INPUT}
                rows={2}
                value={listToLines(q.options)}
                placeholder={'Bright\nLight\nDark'}
                onChange={e => patchQuestion(i, { options: linesToList(e.target.value) })}
              />
              <p className={HINT}>One option per line.</p>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              set('questions', [...form.questions, { prompt: '', max_select: 1, options: [] }])
            }
            className="text-sm font-semibold text-gray-500 hover:text-zoddle-green"
          >
            + Add question
          </button>
        </Field>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-gray-500 px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="text-sm font-semibold bg-zoddle-green text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save ad'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
